import { supabase } from '../auth/supabase'
import { useSyncStatus } from '../sync/status'
import { resyncFromScratch, syncNow } from '../sync/sync'

/**
 * Tells the user whether a tick has left this device.
 *
 * Without it the offline-first design is unnerving rather than reassuring:
 * a box ticked with no network looks exactly like a box that reached the other
 * phone, and the only way to find out is to go and look at the other phone.
 */
export function SyncBadge() {
  const phase = useSyncStatus((s) => s.phase)
  const pending = useSyncStatus((s) => s.pending)
  const lastError = useSyncStatus((s) => s.lastError)

  if (phase === 'disabled') return null

  const label =
    phase === 'syncing'
      ? 'Synchronisation…'
      : phase === 'offline'
        ? pending
          ? `Hors ligne • ${pending} à envoyer`
          : 'Hors ligne'
        : phase === 'error'
          ? pending
            ? `Non synchronisé • ${pending}`
            : 'Non synchronisé'
          : phase === 'signed-out'
            ? 'Déconnecté'
            : phase === 'no-household'
              ? 'Foyer introuvable'
              : pending
                ? `${pending} à envoyer`
                : 'À jour'

  const tone =
    phase === 'error' || phase === 'no-household'
      ? 'error'
      : phase === 'offline'
        ? 'offline'
        : pending
          ? 'pending'
          : 'ok'

  return (
    <button
      class={`sync-badge ${tone}`}
      // The error itself goes in the tooltip rather than the label: Supabase
      // messages are long, and `formatSyncError` exists so that the one person
      // who can act on it can read it instead of "[object Object]".
      title={lastError ?? 'Synchroniser maintenant'}
      onClick={() => void syncNow()}
    >
      <span class="sync-dot" />
      <span>{label}</span>
    </button>
  )
}

/**
 * Forget the cursors and pull the whole household again.
 *
 * The one repair for the failure `CLAUDE.md` §2 describes: a cursor only ever
 * moves forward, so anything a pull failed to store is never asked for again,
 * and the symptom is a green badge over a month view with checkmarks missing.
 * Nothing about it is destructive — pending local changes stay dirty and still
 * push — but it re-downloads everything, so it does not belong on the main
 * screen.
 */
export function ResyncButton() {
  const phase = useSyncStatus((s) => s.phase)
  if (phase === 'disabled') return null

  return (
    <button class="secondary" onClick={() => void resyncFromScratch()}>
      Tout retélécharger
    </button>
  )
}

/** Sign-out control. Lives in *Réglages*, well away from anything tappable by accident. */
export function SignOutButton() {
  const phase = useSyncStatus((s) => s.phase)
  const pending = useSyncStatus((s) => s.pending)

  const client = supabase
  if (phase === 'disabled' || !client) return null

  return (
    <button
      class="secondary"
      onClick={() => {
        // Signing out unbinds this device from the household, so unsent ticks
        // would sit here with nothing to carry them up. Better to say so than
        // to let it be discovered from the other device.
        if (
          pending > 0 &&
          !confirm(`${pending} modification(s) ne sont pas encore synchronisées. Se déconnecter quand même ?`)
        ) {
          return
        }
        // The household binding is dropped by AuthGate's SIGNED_OUT handler and
        // deliberately not here: `App` is still mounted at this point and calls
        // `currentMemberId()` on every render, which throws with no membership.
        // Clearing it early would open a window where a sync pass landing mid
        // sign-out takes the app down.
        void client.auth.signOut()
      }}
    >
      Se déconnecter
    </button>
  )
}
